import { BREAKPOINTS_V3, useMediaQuery } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useRef } from 'react';

import Image from '@/app/components/Image';
import Motion, { type MotionProps } from '@/app/components/Motion';
import Video from '@/app/components/Video/Video';

import type { SectionProps } from './Section';
import { shouldDelayMotionTransition } from './utils/shouldDelayMotionTransition';

export type SectionMediaProps = Pick<
  SectionProps,
  | 'activeIndex'
  | 'altImage'
  | 'id'
  | 'index'
  | 'previousIndex'
  | 'sectionCount'
  | 'videoSrcSet'
>;

export const getAnimateStyles = ({
  index,
  isLargeBreakpoint,
  sectionCount,
}: {
  index: number;
  isLargeBreakpoint: boolean;
  sectionCount: number;
}): Record<string, string | number> => {
  const isFirst = index === 0;
  const isLast = index === sectionCount - 1;
  const isFullscreen = isFirst || isLast;
  if (isLargeBreakpoint) {
    if (isFullscreen) {
      return {
        borderBottomLeftRadius: 0,
        borderTopLeftRadius: 0,
        height: '100vh',
        width: '100vw',
      };
    } else {
      return {
        borderBottomLeftRadius: 8,
        borderTopLeftRadius: 8,
        height: '50vw',
        width: '50vw',
      };
    }
  } else {
    return {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    };
  }
};

export const getMotionProps = ({
  activeIndex,
  delayMotion,
  index,
  isLargeBreakpoint,
  sectionCount,
}: {
  activeIndex: number;
  delayMotion: boolean;
  index: number;
  isLargeBreakpoint: boolean;
  sectionCount: number;
}): MotionProps => {
  return {
    animate: getAnimateStyles({
      index: activeIndex,
      isLargeBreakpoint,
      sectionCount,
    }),
    initial: {
      ...getAnimateStyles({
        index,
        isLargeBreakpoint,
        sectionCount,
      }),
    },
    transition: {
      delay: delayMotion ? 0.5 : 0,
      duration: 0.5,
    },
  };
};

export const shouldDelayOpacityTransition = ({
  activeIndex,
  previousIndex,
  sectionCount,
}: {
  activeIndex: number;
  previousIndex: number;
  sectionCount: number;
}): boolean =>
  (activeIndex === 1 && previousIndex === 0) ||
  (activeIndex === sectionCount - 2 && previousIndex === sectionCount - 1);

const SectionMedia = ({
  activeIndex,
  altImage,
  id,
  index,
  previousIndex,
  sectionCount,
  videoSrcSet,
}: SectionMediaProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const isFirst = index === 0;
  const isLast = index === sectionCount - 1;
  const isFullscreen = isFirst || isLast;
  const isActive = activeIndex === index;
  const isLargeBreakpoint = useMediaQuery(
    `(min-width: ${BREAKPOINTS_V3.large}px)`,
  );

  const delayMotion = shouldDelayMotionTransition({
    activeIndex,
    previousIndex,
    sectionCount,
  });
  const delayOpacity = shouldDelayOpacityTransition({
    activeIndex,
    previousIndex,
    sectionCount,
  });
  const motionProps = getMotionProps({
    activeIndex,
    delayMotion,
    index,
    isLargeBreakpoint: Boolean(isLargeBreakpoint),
    sectionCount,
  });

  return (
    <Motion
      {...motionProps}
      aria-hidden="true"
      className={cx(
        'pointer-events-none lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 lg:overflow-hidden maxlg:!size-auto',
        'lg:nojs:top-0 lg:nojs:h-screen lg:nojs:w-screen',
        {
          'motion-reduce:lg:!h-screen motion-reduce:lg:!w-screen': isFullscreen,
          'lg:max-h-[80vh] lg:max-w-[80vh] motion-reduce:lg:!h-[50vw] motion-reduce:lg:!w-[50vw]':
            !isFullscreen,
        },
      )}
      data-cy="timeline_section_media_frame"
      ref={ref}
    >
      <div
        className={cx(
          'relative h-[500px] w-full overflow-hidden rounded-b-lg',
          'lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 lg:rounded-none lg:js:h-full',
          'lg:nojs:opacity-100 lg:js:transition-opacity lg:js:duration-1000',
          {
            'lg:h-screen lg:w-screen': isFullscreen,
            'lg:aspect-5/4 lg:h-[50vw] lg:w-auto lg:nojs:w-[50vw] lg:nojs:rounded-l-lg':
              !isFullscreen,
            'lg:js:opacity-0': !isActive,
            'lg:js:opacity-100': isActive,
            'lg:js:delay-300': delayOpacity,
          },
        )}
        data-cy="timeline_section_media"
      >
        <Image
          {...altImage}
          className="absolute object-cover motion-safe:hidden"
        />
        <Video
          autoPlay
          className="absolute size-full object-cover motion-reduce:hidden"
          contentTitle=""
          controls={false}
          location={`hiw_section_${id}`}
          loop
          muted
          playsInline
          srcSet={videoSrcSet}
          type="video/mp4"
        />
      </div>
    </Motion>
  );
};

export default SectionMedia;
